import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ServiceMenu from '../components/ServiceMenu';
import ServiceListItem from '../components/ServiceListItem';
import { getActiveServices } from '../services/api';
import './ServiceListPage.css';

function ServiceListPage({ searchQuery, clearSearchQuery }) {
    const [allServices, setAllServices] = useState([]);
    const [filteredMDAsFromMenu, setFilteredMDAsFromMenu] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();
    const mdaIdFilterFromUrl = searchParams.get('mdaId');

    const fetchServices = useCallback(async (mdaId) => {
        setLoading(true);
        setError(null);
        try {
            const params = mdaId ? { mdaId: mdaId, type: 'public' } : { type: 'public' };
            const response = await getActiveServices(params);
            setAllServices(response.data || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load services');
            setAllServices([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchServices(mdaIdFilterFromUrl);
    }, [fetchServices, mdaIdFilterFromUrl]);

    const handleFilterUpdate = useCallback((selectedMDAs) => {
        setFilteredMDAsFromMenu(selectedMDAs);
    }, []);

    const displayedServices = useMemo(() => {
        let services = allServices;

        if (filteredMDAsFromMenu.length > 0) {
            services = services.filter(service =>
                service.mda?.fullName && filteredMDAsFromMenu.includes(service.mda.fullName)
            );
        }

        if (searchQuery) {
            const lowercasedQuery = searchQuery.toLowerCase();
            services = services.filter(service =>
                service.name.toLowerCase().includes(lowercasedQuery) ||
                (service.mda?.fullName && service.mda.fullName.toLowerCase().includes(lowercasedQuery)) ||
                (service.mda?.shortName && service.mda.shortName.toLowerCase().includes(lowercasedQuery))
            );
        }

        return services;
    }, [allServices, filteredMDAsFromMenu, searchQuery]);

    if (loading && allServices.length === 0) return <p>Loading...</p>;
    if (error) return <p>Error: {error}</p>;

    return (
        <div className="service-list-page-container">
            <div className="service-list-sidebar">
                <ServiceMenu
                    services={allServices}
                    onFilterChange={handleFilterUpdate}
                />
            </div>
            <div className="service-list-main">
                <div className="service-list-content">
                    <h1>
                        Available Services
                        {mdaIdFilterFromUrl && <span style={{ fontSize: '0.8em', fontWeight: 'normal', marginLeft: '15px', color: '#555' }}>(Filtered by Provider)</span>}
                    </h1>
                    {loading && <p>Updating list...</p>}
                    {displayedServices.length === 0 && !loading ? (
                        <div>
                            <p>No services match the current criteria or none are available.</p>
                            {searchQuery && <button onClick={clearSearchQuery}>Clear Search</button>}
                        </div>
                    ) : (
                        <ul style={{ listStyle: 'none', padding: 0 }}>
                            {displayedServices.map(service => (
                                <ServiceListItem key={service.id} service={service} />
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ServiceListPage;
